<template>
  <div v-show="isShowPressure" ref="wrapper" class="chart-pressure">
    <div v-show="hasData">
      <div style="position: relative; width: 100%">
        <div ref="chart" class="chart-pressure__chart" />
      </div>
    </div>
  </div>
</template>

<script>
  import resize from 'vue-resize-directive';

  import { PressureDrawer } from '@/uikitProject/charts/new/PressureDrawer';
  import { isInjectionInProgress } from '@/utils/injectionHelpers';
  import { UNKNOWN_VALUE } from '@/utils/chart/chart-constants';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import { sendDebugEvent } from '@/utils/sentryHelpers';
  import { mergeDataObjects } from '@/utils/measurementHelpers';

  const EVENT_CURRENT_PRESSURE = 'currentPressure';

  export default {
    name: 'PrChartPressure',

    directives: { resize },

    props: {
      measurement: Object,
      injection: {
        type: Object,
      },
      method: {
        type: Object,
      },
      timeMax: {
        type: Number,
      },
      initialZoom: {
        type: Object,
      },
      hasLiveUpdate: {
        type: Boolean,
        default: true,
      },
      sampleToken: {
        type: String,
        default: null,
      },
      width: {
        type: Number,
      },
      isShowPressure: {
        type: Boolean,
      },
    },

    data() {
      return {
        drawer: null,
        internalMeasurement: this.measurement?.data ? this.measurement : null,

        subscriptionForMeasurement: null,
        subscriptionForMeasurementsData: null,
      };
    },

    computed: {
      hasData() {
        return this.internalMeasurement?.data_length > 0;
      },
      data() {
        return this.internalMeasurement?.data;
      },
      expectedDurationSeconds() {
        if (this.timeMax != null && Number.isFinite(this.timeMax)) {
          return this.timeMax * 60;
        }
        if (this.method?.run_time_min != null && Number.isFinite(this.method.run_time_min)) {
          return this.method.run_time_min * 60;
        }
        if (this.injection?.expected_duration_sec != null) {
          return this.injection.expected_duration_sec;
        }
        if (this.injection?.expected_duration_ms != null) {
          return this.injection.expected_duration_ms / 1000;
        }
        return 0;
      },
      isInjectionInProgress() {
        return isInjectionInProgress(this.injection);
      },
    },

    watch: {
      async isShowPressure() {
        await this.$nextTick();
        this.initChart();
      },
      timeMax() {
        this.refresh();
      },

      data(data) {
        this.$emit(EVENT_CURRENT_PRESSURE, data ? data[data.length - 1] : null);
        this.refresh();
      },
      width(value) {
        this.drawer?.actions.setWidth(value);
      },
      measurement(m, oldM) {
        if (m === this.internalMeasurement) return;

        const idChanged = oldM?.id !== m?.id;

        if (!idChanged && m.data == null) return;
        this.internalMeasurement = m;

        if (idChanged && m?.id) {
          this.initMeasurementData(this.measurement.id, this.measurement.ulid);
        }
      },
      isInjectionInProgress(isInProgress) {
        isInProgress
          ? this.subscriptionForMeasurementsData.resume()
          : this.subscriptionForMeasurementsData.pause();
      },
    },

    created() {
      if (this.measurement?.ulid != null) {
        this.initMeasurementData(this.measurement.id, this.measurement.ulid);
      }
    },

    beforeDestroy() {
      this.stopSubscriptions();
    },

    methods: {
      initChart() {
        if (this.internalMeasurement?.data == null) {
          return;
        }

        if (this.$refs.wrapper == null) {
          sendDebugEvent('#2413: Front: Fix sentry issues', [
            ['measurementId', this.internalMeasurement?.id],
            [
              'Sentry issue',
              'https://newcrom.sentry.io/issues/5729846107/?project=6192768&query=is:unresolved+issue.priority:%5Bhigh,+medium%5D&statsPeriod=30d&stream_index=2&utc=true',
            ],
          ]);

          return;
        }

        this.drawer = new PressureDrawer({
          container: this.$refs.chart,
          measurement: this.internalMeasurement,
          expectedDurationSeconds: this.expectedDurationSeconds,
          isInProgress: isInjectionInProgress(this.injection),
          widthPx: this.$refs.wrapper.getBoundingClientRect().width,
        });

        this.zoom(this.initialZoom);
      },

      refresh() {
        if (this.internalMeasurement?.data == null || !this.isShowPressure) {
          return;
        }

        if (this.drawer) {
          this.drawer.actions.setIsInProgress(isInjectionInProgress(this.injection));
          this.drawer.actions.setExpectedDurationSeconds(this.expectedDurationSeconds);
          this.drawer.refresh(this.internalMeasurement);
        } else {
          this.initChart();
        }
      },

      zoom(zoomBounds) {
        this.drawer?.actions.zoom({
          x0: zoomBounds?.x0 ?? null,
          x1: zoomBounds?.x1 ?? null,
        });
      },

      async onMeasurement(m) {
        const _data = await m.getData(false);

        const data = this.internalMeasurement?.data
          ? _data?.length >= this.internalMeasurement.data?.length
            ? _data
            : this.internalMeasurement.data
          : _data;

        this.internalMeasurement = {
          ...this.internalMeasurement,
          ...m,
          data,
          data_length: data.length,
        };

        this.refresh();
      },

      initMeasurementData(measurementId, measurementUlid) {
        this.stopSubscriptions();

        this.initSubscriptionForMeasurement(measurementId);
        this.initSubscriptionForMeasurementData(measurementUlid);
      },
      stopSubscriptions() {
        this.subscriptionForMeasurement?.unsubscribe();
        this.subscriptionForMeasurementsData?.unsubscribe();
      },

      initSubscriptionForMeasurementData(measurementUlid) {
        if (!this.hasLiveUpdate) {
          return;
        }

        const onNewData = (from, values) => {
          const currentData = this.internalMeasurement?.data;
          const currentLength = currentData?.length ?? 0;

          if (currentLength === from) {
            const data = [...(currentData ?? []), ...values];
            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          } else if (currentLength < from) {
            const difference = from - currentLength;
            const unknownValues = Array(difference).fill(UNKNOWN_VALUE);
            const data = [...(currentData ?? []), ...unknownValues, ...values];

            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          } else {
            const data = [...(currentData ?? [])];
            data.splice(from, values.length, ...values);

            this.internalMeasurement = {
              ...this.internalMeasurement,
              data,
              data_length: data.length,
            };
          }
        };

        this.subscriptionForMeasurementsData = apiMeasurements.subscribeForMeasurementsData(
          measurementUlid,
          (data) => {
            if (data?.length !== 0) {
              onNewData(data[0].from, data.length === 1 ? data[0].values : mergeDataObjects(data));
            }
          },
          this.sampleToken,
          this.injection.sample_id,
          { isPaused: !this.isInjectionInProgress },
        );
      },
      initSubscriptionForMeasurement(measurementId) {
        this.subscriptionForMeasurement = apiMeasurements.subscribeForMeasurement(
          measurementId,
          (measurement) => {
            this.onMeasurement(measurement);
          },
          this.sampleToken,
          this.injection.sample_id,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .chart-pressure {
    &__chart {
      width: 100%;
      height: 100px;
    }
  }
</style>
